import { CompanyType } from "../../types/payments";
import { isValidLocalPhoneNumber } from "../../utils/countries";
import {
  FISCAL_ID_LENGTH,
  IBAN_MAX_LENGTH,
  IBAN_MIN_LENGTH,
  MIN_NAME_LENGTH,
  POSTAL_CODE_LENGTH
} from "../../constants";

/**
 * Values held by the configuration form.
 *
 * `country` is validated but never sent: the API does not accept it, yet the
 * mobile app requires it before allowing a submit, so it is kept for parity.
 */
export interface ConfigurationFormValues {
  firstName: string;
  lastName: string;
  easypayCompanyType: CompanyType | "";
  iban: string;
  fiscalId: string;
  organizationName: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  phoneCountry: string;
  phoneNumber: string;
}

export type ConfigurationFormErrors = Partial<
  Record<keyof ConfigurationFormValues, string>
>;

const isOnlyDigits = (value: string) => /^\d+$/.test(value);

/**
 * Returns a translation key per invalid field, or an empty object when the form
 * can be submitted. The rules mirror the ones the mobile app enforces, which
 * are the ones the API expects.
 */
export function validateConfigurationForm(
  values: ConfigurationFormValues
): ConfigurationFormErrors {
  const errors: ConfigurationFormErrors = {};

  if (values.firstName.trim().length < MIN_NAME_LENGTH) {
    errors.firstName = "paymentConfigurationForm.errors.tooShort";
  }
  if (values.lastName.trim().length < MIN_NAME_LENGTH) {
    errors.lastName = "paymentConfigurationForm.errors.tooShort";
  }
  if (!values.easypayCompanyType) {
    errors.easypayCompanyType = "paymentConfigurationForm.errors.required";
  }
  if (
    values.iban.length < IBAN_MIN_LENGTH ||
    values.iban.length > IBAN_MAX_LENGTH
  ) {
    errors.iban = "paymentConfigurationForm.errors.iban";
  }
  if (
    values.fiscalId.length !== FISCAL_ID_LENGTH ||
    !isOnlyDigits(values.fiscalId)
  ) {
    errors.fiscalId = "paymentConfigurationForm.errors.fiscalId";
  }
  if (values.organizationName.trim().length < MIN_NAME_LENGTH) {
    errors.organizationName = "paymentConfigurationForm.errors.tooShort";
  }
  if (values.address.trim().length < MIN_NAME_LENGTH) {
    errors.address = "paymentConfigurationForm.errors.tooShort";
  }
  if (
    values.postalCode.length !== POSTAL_CODE_LENGTH ||
    !values.postalCode.includes("-")
  ) {
    errors.postalCode = "paymentConfigurationForm.errors.postalCode";
  }
  if (values.city.trim().length < MIN_NAME_LENGTH) {
    errors.city = "paymentConfigurationForm.errors.tooShort";
  }
  if (values.country.length !== 2) {
    errors.country = "paymentConfigurationForm.errors.required";
  }
  // Validated against the country's own rules, like the mobile does, rather
  // than by a fixed minimum length.
  if (!isValidLocalPhoneNumber(values.phoneCountry, values.phoneNumber)) {
    errors.phoneNumber = "paymentConfigurationForm.errors.phone";
  }

  return errors;
}
